import 'client-only';

import { getDeviceId } from './deviceId';

interface ProxyFetchInit extends Omit<RequestInit, 'body'> {
  /** Plain JSON body — serialized + `Content-Type: application/json` set. */
  body?: unknown;
}

/**
 * Client → visitor-API-proxy fetch. Attaches the caller's Firebase ID token as
 * `Authorization: Bearer <token>` — the SAME source and lazy firebase-load as
 * {@link withClientAPI}, so firebase stays out of the visitor's first-paint
 * bundle — then calls a same-origin `/api/…` route handler and returns the slim
 * JSON view model.
 *
 * The point: the generated `@ps/api` client (fat transport types + the shared
 * runtime) never enters the visitor browser bundle. The route handler owns the
 * generated client server-side (`withProxyAPI`) and returns only the few fields
 * the view needs. An absent Firebase user → no `Authorization` header → the
 * handler runs anonymous (public reads still succeed).
 */
export async function proxyFetch<TResponse>(
  path: string,
  init?: ProxyFetchInit,
): Promise<TResponse> {
  const { body, headers, ...rest } = init ?? {};
  const token = await currentFirebaseIdToken();

  const deviceId = getDeviceId();

  const response = await fetch(path, {
    ...rest,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(deviceId ? { 'X-Device-ID': deviceId } : {}),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const error = new Error(
      `proxyFetch ${path} failed with ${response.status}${
        detail ? `: ${detail}` : ''
      }`,
    );
    console.error('[proxyFetch] request failed:', error);
    throw error;
  }

  // A 204 / empty-body response (e.g. a write that returns nothing) has no JSON
  // to parse — hand back undefined rather than throwing on `response.json()`.
  if (response.status === 204) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}

/**
 * The current Firebase ID token, or `null` when signed out. Lazy-imports the
 * firebase SDK so it loads on the FIRST proxy request (post-hydration), not in
 * the visitor's first-load bundle — identical to `withClientAPI`'s resolver.
 */
async function currentFirebaseIdToken(): Promise<string | null> {
  const { auth } = await import('../firebase/auth');
  const authInstance = auth();
  await authInstance.authStateReady();
  return (await authInstance.currentUser?.getIdToken()) ?? null;
}
